import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CustomEditor } from '../../core/CustomEditor';
import { DocumentCard } from '../../DocumentPortal/DocumentSortingDialog/DocumentCard';
import type { IDocument } from '../../Interfaces/IDocument';
import {
  initSendDocumentNew,
  ISendDocumentDocumentTypeSchema,
} from '../../Interfaces/ISendDocumentNew';
import type { ILegalClient } from '../../Interfaces/ILegalClient';
import type { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import type { IInvoiceFullObject } from '../../Interfaces/IInvoice';
import type { IRealEstateLandRegisterEntryQuery } from '../../Interfaces/IRealEstateLandRegisterEntryQuery';
import { useGetSystemMail } from '../../hooks/data';
import { useGetRealEstateLandRegisterEntryQueryByRealEstate } from '../../hooks/reactQuerys/RealEstateQueries';

type DocumentType = (typeof ISendDocumentDocumentTypeSchema.options)[number];

interface ISendDocumentProps {
  legalTransactionFullObject: ILegalTransactionFullObject;
  sendDocument: ReturnType<typeof initSendDocumentNew>;
  setSendDocument: React.Dispatch<
    React.SetStateAction<ReturnType<typeof initSendDocumentNew>>
  >;
  enabled?: boolean;
}

interface IEmailEditorProps {
  emailText: string;
  setSendDocument: React.Dispatch<
    React.SetStateAction<ReturnType<typeof initSendDocumentNew>>
  >;
}

export const convertInvoiceToDocument = (
  inv: IInvoiceFullObject
): IDocument => ({
  idDocument: inv.idDocument,
  FileName: inv.FileName,
  idMIMEType: inv.idMIMEType,
  Created_at: inv.Created_at,
  idDocumentDirectory: null,
  idLegalTransaction: inv.idLegalTransaction,
  perEmail: false,
  isFromTemplate: false,
});

const uniqueByIdDocument = (docs: IDocument[]): IDocument[] =>
  Array.from(new Map(docs.map(d => [d.idDocument, d])).values());

const EmailEditor = React.memo(
  ({ emailText, setSendDocument }: IEmailEditorProps) => {
    const [localEmailText, setLocalEmailText] = useState(emailText);
    // Tracks the last value we sent up to the parent so we can distinguish
    // external template loads (emailText changed by parent) from our own
    // debounced updates (which must not trigger a setContent reset in TipTap).
    const lastSentRef = useRef(emailText);

    useEffect(() => {
      // Only sync down when emailText was changed externally, not by ourselves.
      if (emailText !== lastSentRef.current) {
        setLocalEmailText(emailText);
      }
    }, [emailText]);

    useEffect(() => {
      if (localEmailText === emailText) return;

      const timeout = window.setTimeout(() => {
        lastSentRef.current = localEmailText;
        setSendDocument(prev => ({ ...prev, emailText: localEmailText }));
      }, 300);

      return () => window.clearTimeout(timeout);
    }, [emailText, localEmailText, setSendDocument]);

    if (emailText === '') {
      return null;
    }

    return (
      <Box mt={2}>
        <CustomEditor
          txt={localEmailText}
          setTxt={setLocalEmailText}
          dictonaryIdsArray={[1, 2, 3]}
        />
      </Box>
    );
  }
);

export const SendDocumentNew: React.FC<ISendDocumentProps> = ({
  legalTransactionFullObject,
  sendDocument,
  setSendDocument,
  enabled = true,
}) => {
  const [selectedStandaloneTypes, setSelectedStandaloneTypes] = useState<
    DocumentType[]
  >([]);
  const [selectedDocuments, setSelectedDocuments] = useState<IDocument[]>([]);
  const [selectedLegalClients, setSelectedLegalClients] = useState<
    ILegalClient[]
  >([]);

  const { systemMail, isLoadingSystemMail } = useGetSystemMail(31);

  const certificates = useMemo(
    () => legalTransactionFullObject.CertificateArray || [],
    [legalTransactionFullObject.CertificateArray]
  );

  const invoices = useMemo(() => {
    const a = legalTransactionFullObject.InvoiceArray || [];
    const b = (legalTransactionFullObject.InvoiceObjectArray || []).map(
      convertInvoiceToDocument
    );
    return uniqueByIdDocument([...a, ...b]);
  }, [
    legalTransactionFullObject.InvoiceArray,
    legalTransactionFullObject.InvoiceObjectArray,
  ]);

  const drafts = useMemo(
    () => legalTransactionFullObject.DraftArray || [],
    [legalTransactionFullObject.DraftArray]
  );

  const otherDocuments = useMemo(
    (): IDocument[] =>
      uniqueByIdDocument(legalTransactionFullObject.OtherDocumentArray || []),
    [legalTransactionFullObject.OtherDocumentArray]
  );

  const docsFromRequirements = useMemo(() => {
    const flat =
      legalTransactionFullObject.LegalPhaseArray?.flatMap(
        phase =>
          phase.LegalRequirementArray?.flatMap(
            req => req.DocumentArrayEmployees || []
          ) || []
      ) || [];

    return uniqueByIdDocument(flat);
  }, [legalTransactionFullObject.LegalPhaseArray]);

  const internalStorageDocs = useMemo((): IDocument[] => {
    const raw = legalTransactionFullObject.DocumentArray as unknown as
      | Array<
          Partial<IDocument> & {
            idDocument?: number;
            FileName?: string;
            idMIMEType?: number | string;
          }
        >
      | undefined;

    const mapped: IDocument[] = (raw || [])
      .filter(d => typeof d.idDocument === 'number' && d.idDocument > 0)
      .map(d => {
        const mt =
          typeof d.idMIMEType === 'string'
            ? Number(d.idMIMEType)
            : d.idMIMEType;

        return {
          idDocument: d.idDocument as number,
          FileName: (d.FileName || '').toString(),
          idMIMEType: Number.isFinite(mt) ? (mt as number) : 1,
          Created_at: (d as { Created_at?: string }).Created_at ?? '',
          idDocumentDirectory:
            (d as { idDocumentDirectory?: number | null })
              .idDocumentDirectory ?? null,
          idLegalTransaction:
            (d as { idLegalTransaction?: number | null }).idLegalTransaction ??
            legalTransactionFullObject.idLegalTransaction,
          perEmail: (d as { perEmail?: boolean }).perEmail ?? false,
          isFromTemplate:
            (d as { isFromTemplate?: boolean }).isFromTemplate ?? false,
        };
      });

    return uniqueByIdDocument(mapped);
  }, [
    legalTransactionFullObject.DocumentArray,
    legalTransactionFullObject.idLegalTransaction,
  ]);

  const realEstateIds = useMemo(() => {
    const ids =
      legalTransactionFullObject.RelationshipArray?.flatMap(rel =>
        (rel.RealEstateArray || []).map(re => re.idRealEstate)
      ) || [];

    return Array.from(new Set(ids)).filter(
      (id): id is number => typeof id === 'number' && Number.isFinite(id)
    );
  }, [legalTransactionFullObject.RelationshipArray]);

  const {
    realEstateLandRegisterEntryQueries: landRegisterQueryItems,
    isLoadingRealEstateLandRegisterEntryQueries: isLoadingLandRegisterDocs,
  } = useGetRealEstateLandRegisterEntryQueryByRealEstate(
    realEstateIds,
    enabled
  );

  const landRegisterDocs: IDocument[] = useMemo(() => {
    if (!enabled) return [];

    const mapped: IDocument[] = (landRegisterQueryItems || [])
      .filter(
        (
          item
        ): item is IRealEstateLandRegisterEntryQuery & {
          idDocument: number;
          FileName: string;
          idMIMEType: number | string;
        } =>
          typeof item.idDocument === 'number' &&
          item.idDocument > 0 &&
          typeof item.FileName === 'string' &&
          item.FileName.trim().length > 0
      )
      .map(item => {
        const mt =
          typeof item.idMIMEType === 'string'
            ? Number(item.idMIMEType)
            : item.idMIMEType;

        return {
          idDocument: item.idDocument,
          FileName: item.FileName,
          idMIMEType: Number.isFinite(mt) ? mt : 1,
          Created_at: item.Created_at ?? '',
          idDocumentDirectory: null,
          idLegalTransaction: legalTransactionFullObject.idLegalTransaction,
          perEmail: false,
          isFromTemplate: false,
        };
      });

    return uniqueByIdDocument(mapped);
  }, [
    enabled,
    landRegisterQueryItems,
    legalTransactionFullObject.idLegalTransaction,
  ]);

  const isDocSelected = (doc: IDocument) =>
    selectedDocuments.some(d => d.idDocument === doc.idDocument);

  const setDocSelected = (doc: IDocument, selected: boolean) => {
    setSelectedDocuments(prev => {
      if (selected) {
        return prev.some(d => d.idDocument === doc.idDocument)
          ? prev
          : [...prev, doc];
      }

      return prev.filter(d => d.idDocument !== doc.idDocument);
    });
  };

  const toggleSelectAll = (docs: IDocument[]) => {
    setSelectedDocuments(prev => {
      const prevIds = new Set(prev.map(d => d.idDocument));
      const docIds = new Set(docs.map(d => d.idDocument));
      const allSelected =
        docs.length > 0 && docs.every(d => prevIds.has(d.idDocument));

      if (allSelected) {
        return prev.filter(d => !docIds.has(d.idDocument));
      }

      const toAdd = docs.filter(d => !prevIds.has(d.idDocument));
      return [...prev, ...toAdd];
    });
  };

  const toggleStandaloneType = (type: DocumentType, checked: boolean) => {
    setSelectedStandaloneTypes(prev =>
      checked ? [...prev, type] : prev.filter(t => t !== type)
    );
  };

  const selectedGroupTypes = useMemo(() => {
    const types: DocumentType[] = [];

    if (certificates.some(isDocSelected)) types.push('Record');
    if (invoices.some(isDocSelected)) types.push('Invoice');
    if (drafts.some(isDocSelected)) types.push('Drafts');
    if (docsFromRequirements.some(isDocSelected)) {
      types.push('DocumentsFromRequirements');
    }

    return types;
  }, [certificates, invoices, drafts, docsFromRequirements, selectedDocuments]);

  const finalDocumentTypes = useMemo(
    () =>
      Array.from(new Set([...selectedGroupTypes, ...selectedStandaloneTypes])),
    [selectedGroupTypes, selectedStandaloneTypes]
  );

  const totalSelectedCount = useMemo(
    () => selectedDocuments.length + selectedStandaloneTypes.length,
    [selectedDocuments.length, selectedStandaloneTypes.length]
  );

  useEffect(() => {
    setSendDocument(prev => ({
      ...prev,
      legalClientArray: selectedLegalClients,
    }));
  }, [selectedLegalClients, setSendDocument]);

  useEffect(() => {
    const uniqueDocs = uniqueByIdDocument(selectedDocuments);
    const ids = uniqueDocs.map(d => d.idDocument);

    setSendDocument(prev => ({
      ...prev,
      selectedDocumentIds: ids,
      selectedDocuments: uniqueDocs.map(d => ({
        idDocument: d.idDocument,
        ...(typeof d.FileName === 'string' && d.FileName.length > 0
          ? { FileName: d.FileName }
          : {}),
      })),
    }));
  }, [selectedDocuments, setSendDocument]);

  useEffect(() => {
    setSendDocument(prev => ({
      ...prev,
      documentTypeArray: finalDocumentTypes,
    }));
  }, [finalDocumentTypes, setSendDocument]);

  useEffect(() => {
    if (systemMail && sendDocument.emailText === '') {
      setSendDocument(prev => ({
        ...prev,
        emailText: systemMail.Body,
        subject: systemMail.Subject,
      }));
    }
  }, [systemMail, sendDocument.emailText, setSendDocument]);

  if (isLoadingSystemMail) {
    return (
      <Box
        sx={{
          height: '30vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!systemMail) {
    return (
      <Box
        sx={{
          height: '30vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography>
          Es gab einen Fehler beim Laden der automatischen Email
        </Typography>
      </Box>
    );
  }

  const renderGroup = (groupTitle: string, docs: IDocument[]) => {
    const selectedCount = docs.filter(isDocSelected).length;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display='flex' alignItems='center' width='100%'>
            <Typography sx={{ flexGrow: 1 }}>
              {groupTitle}{' '}
              {selectedCount > 0 && <em>({selectedCount} ausgewählt)</em>}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={docs.length > 0 && docs.every(isDocSelected)}
                  onChange={() => toggleSelectAll(docs)}
                  onClick={e => e.stopPropagation()}
                />
              }
              label='alle auswählen'
            />
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          {docs.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Keine Dokumente vorhanden
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {docs.map(doc => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={`${groupTitle}-${doc.idDocument}`}
                >
                  <DocumentCard
                    initialDocument={doc}
                    selectable
                    selected={isDocSelected(doc)}
                    onSelectChange={selected => setDocSelected(doc, selected)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderNonExpandableStandalone = (
    groupTitle: string,
    type: DocumentType
  ) => (
    <Accordion expanded={false} disableGutters>
      <AccordionSummary expandIcon={null}>
        <Box display='flex' alignItems='center' width='100%'>
          <Typography sx={{ flexGrow: 1 }}>{groupTitle}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                disabled={groupTitle === 'Digitale Nebenakte'}
                checked={selectedStandaloneTypes.includes(type)}
                onChange={e => toggleStandaloneType(type, e.target.checked)}
                onClick={e => e.stopPropagation()}
              />
            }
            label='auswählen'
          />
        </Box>
      </AccordionSummary>
    </Accordion>
  );

  const renderMandantenAccordion = () => {
    const legalClients = legalTransactionFullObject.LegalClientArray || [];

    const clientColumns: GridColDef[] = [
      { field: 'idLegalClient', headerName: 'Nr.', width: 100 },
      {
        field: 'FirstName',
        headerName: 'Vorname',
        flex: 1,
        valueGetter: p => p.row.FirstName || '',
      },
      {
        field: 'LastName',
        headerName: 'Nachname',
        flex: 1,
        valueGetter: p => p.row.LastName || '',
      },
      {
        field: 'Email',
        headerName: 'E-Mail',
        flex: 1,
        valueGetter: p => p.row.Email || '',
      },
    ];

    const selectedClientCount = legalClients.filter(
      c =>
        c.Email &&
        selectedLegalClients.some(sc => sc.idLegalClient === c.idLegalClient)
    ).length;

    return (
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            Mandanten{' '}
            {selectedClientCount > 0 && (
              <em>({selectedClientCount} ausgewählt)</em>
            )}
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={legalClients}
              columns={clientColumns}
              getRowId={row => row.idLegalClient}
              checkboxSelection
              disableRowSelectionOnClick
              isRowSelectable={params => !!params.row.Email}
              onRowSelectionModelChange={newSelection => {
                const selected = legalClients.filter(c =>
                  newSelection.includes(c.idLegalClient)
                );
                setSelectedLegalClients(selected);
              }}
              getRowClassName={params =>
                !params.row.Email ? 'notSelectableRow' : ''
              }
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            Dokumente{' '}
            {totalSelectedCount > 0 && (
              <em>({totalSelectedCount} ausgewählt)</em>
            )}
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {enabled && isLoadingLandRegisterDocs && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {renderGroup('Urkunden', certificates)}
          {renderGroup('Rechnungen', invoices)}
          {renderGroup('Entwürfe', drafts)}
          {renderGroup('Öffentliche Dokumente', otherDocuments)}
          {renderGroup('Dokumente aus Maßnahmen', docsFromRequirements)}
          {renderGroup('Interne Datenablage', internalStorageDocs)}
          {renderGroup('Grundbuchabfragen', landRegisterDocs)}

          {renderNonExpandableStandalone(
            'E-Mails aus Maßnahmen',
            'EmailsFromRequirements'
          )}
          {renderNonExpandableStandalone('Digitale Nebenakte', 'SideFile')}
        </AccordionDetails>
      </Accordion>

      {renderMandantenAccordion()}

      <EmailEditor
        emailText={sendDocument.emailText}
        setSendDocument={setSendDocument}
      />
    </>
  );
};
